import { ICharacteristic } from 'models/product'

const normalizeName = (name?: string): string => (name ?? '').trim().toLowerCase()

const normalizeValue = (value?: string): string => (value ?? '').trim().toLowerCase()

/**
 * Значение характеристики по её названию (без учёта регистра и пробелов)
 */
export const getCharacteristicValue = (
  characteristics: ICharacteristic[] | undefined,
  name: string | undefined
): string => {
  if (!name) return ''
  return (
    characteristics?.find((c) => normalizeName(c?.name) === normalizeName(name))
      ?.value ?? ''
  )
}

/**
 * Вариативные характеристики нигде не хранятся отдельно: у вариации в characteristic
 * лежат вперемешку и её собственные значения, и унаследованные от основного товара.
 * Поэтому вычисляем оси по данным — вариативная та, значения которой различаются
 * у основного товара и вариаций; порядок берём из основного товара
 * @param parentCharacteristics - характеристики основного товара
 * @param variations - вариации товара с их характеристиками
 * @returns до двух названий характеристик для заголовков таблицы вариаций
 */
export const deriveVariationAxes = (
  parentCharacteristics: ICharacteristic[] = [],
  variations: Array<{ characteristic?: ICharacteristic[] }> = []
): string[] => {
  const displayName = new Map<string, string>()
  const valuesByName = new Map<string, Set<string>>()
  const rowsWithName = new Map<string, number>()
  const order: string[] = []

  const collect = (
    characteristics: ICharacteristic[] | undefined,
    isVariationRow: boolean
  ) => {
    const countedInRow = new Set<string>()
    characteristics?.forEach((char) => {
      const key = normalizeName(char?.name)
      if (!key) return
      if (!displayName.has(key)) {
        displayName.set(key, char.name.trim())
        order.push(key)
      }
      if (!valuesByName.has(key)) valuesByName.set(key, new Set())
      // незаполненное значение не делает характеристику вариативной
      const value = normalizeValue(char.value)
      if (value) valuesByName.get(key)?.add(value)
      if (isVariationRow && !countedInRow.has(key)) {
        countedInRow.add(key)
        rowsWithName.set(key, (rowsWithName.get(key) ?? 0) + 1)
      }
    })
  }

  collect(parentCharacteristics, false)
  variations.forEach((variation) => collect(variation?.characteristic, true))

  const varying = order.filter((key) => (valuesByName.get(key)?.size ?? 0) > 1)

  if (varying.length) {
    return varying.slice(0, 2).map((key) => displayName.get(key) as string)
  }

  // Все значения совпали (например, вариация пока одна): берём одну ось —
  // вторая колонка без различающихся значений только дублировала бы первую
  const fallback =
    order.find((key) => rowsWithName.get(key) === variations.length) ?? order[0]

  return fallback ? [displayName.get(fallback) as string] : []
}
